import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { matrimoniosAPI } from "@/lib/api";
import { ArrowLeft, Edit, Printer, HeartHandshake, Calendar, MapPin, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function MatrimonioDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [matrimonio, setMatrimonio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMatrimonio();
  }, [id]);

  const loadMatrimonio = async () => {
    try {
      const response = await matrimoniosAPI.getOne(id);
      setMatrimonio(response.data);
    } catch (error) {
      toast.error("Error al cargar el matrimonio");
      navigate("/matrimonios");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando...</div>
      </div>
    );
  }

  if (!matrimonio) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="matrimonio-detail-page">
      {/* Header - No print */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/matrimonios")}
            className="h-10 w-10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
              <HeartHandshake className="w-8 h-8 text-rose-600" strokeWidth={1.5} />
              Acta de Matrimonio
            </h1>
            <p className="text-stone-600 mt-1">Partida #{matrimonio.numero_partida}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/matrimonios/${id}/editar`}>
            <Button variant="outline" className="h-10">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
          <Button onClick={handlePrint} className="h-10 bg-rose-950 hover:bg-rose-900 text-white">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Acta
          </Button>
        </div>
      </div>

      {/* Print Certificate */}
      <div className="print-certificate">
        {/* Print Header */}
        <div className="print-header hidden print:block text-center mb-8">
          <h1 className="text-3xl font-serif text-rose-950">ACTA DE MATRIMONIO</h1>
          <p className="text-stone-600 mt-2">Parroquia San Juan Bautista</p>
          <p className="text-stone-500 text-sm">Diócesis de San Juan</p>
        </div>

        {/* Datos del Registro */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-stone-800">
                Datos del Registro
              </CardTitle>
              <Badge variant="outline" className="text-rose-600 border-rose-200 bg-rose-50">
                Libro {matrimonio.numero_libro} · Folio {matrimonio.numero_folio} · Partida {matrimonio.numero_partida}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-stone-400 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha</p>
                <p className="text-stone-800 font-medium">{matrimonio.fecha_matrimonio}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 md:col-span-3">
              <MapPin className="w-5 h-5 text-stone-400 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar</p>
                <p className="text-stone-800 font-medium">{matrimonio.lugar_matrimonio}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contrayentes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Esposo */}
          <Card className="border-stone-200 bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Esposo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Nombre Completo</p>
                <p className="text-xl font-serif text-stone-800 mt-1">
                  {matrimonio.esposo_nombre} {matrimonio.esposo_apellido}
                </p>
              </div>
              {matrimonio.esposo_fecha_nacimiento && (
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha de Nacimiento</p>
                  <p className="text-stone-800 mt-1">{matrimonio.esposo_fecha_nacimiento}</p>
                </div>
              )}
              {matrimonio.esposo_lugar_nacimiento && (
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar de Nacimiento</p>
                  <p className="text-stone-800 mt-1">{matrimonio.esposo_lugar_nacimiento}</p>
                </div>
              )}
              {matrimonio.esposo_estado_civil_previo && (
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Estado Civil Previo</p>
                  <p className="text-stone-800 mt-1">{matrimonio.esposo_estado_civil_previo}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Esposa */}
          <Card className="border-stone-200 bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
                <User className="w-5 h-5 text-rose-600" />
                Esposa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Nombre Completo</p>
                <p className="text-xl font-serif text-stone-800 mt-1">
                  {matrimonio.esposa_nombre} {matrimonio.esposa_apellido}
                </p>
              </div>
              {matrimonio.esposa_fecha_nacimiento && (
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha de Nacimiento</p>
                  <p className="text-stone-800 mt-1">{matrimonio.esposa_fecha_nacimiento}</p>
                </div>
              )}
              {matrimonio.esposa_lugar_nacimiento && (
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar de Nacimiento</p>
                  <p className="text-stone-800 mt-1">{matrimonio.esposa_lugar_nacimiento}</p>
                </div>
              )}
              {matrimonio.esposa_estado_civil_previo && (
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Estado Civil Previo</p>
                  <p className="text-stone-800 mt-1">{matrimonio.esposa_estado_civil_previo}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Testigos y Ministro */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Testigos y Ministro
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Testigo 1</p>
              <p className="text-stone-800 mt-1">{matrimonio.testigo1_nombre || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Testigo 2</p>
              <p className="text-stone-800 mt-1">{matrimonio.testigo2_nombre || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Ministro Celebrante</p>
              <p className="text-stone-800 font-medium mt-1">{matrimonio.ministro_nombre}</p>
            </div>
          </CardContent>
        </Card>

        {/* Notas */}
        {matrimonio.notas_marginales && (
          <Card className="border-stone-200 bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium text-stone-800">
                Notas Marginales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-stone-600 whitespace-pre-wrap">{matrimonio.notas_marginales}</p>
            </CardContent>
          </Card>
        )}

        {/* Print Signature Area */}
        <div className="print-signature hidden print:flex justify-between mt-16 pt-8">
          <div className="text-center">
            <div className="w-48 border-t border-stone-400 pt-2">
              <p className="text-sm text-stone-600">Firma del Párroco</p>
            </div>
          </div>
          <div className="text-center">
            <div className="w-48 border-t border-stone-400 pt-2">
              <p className="text-sm text-stone-600">Sello Parroquial</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
